import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import noop from 'lodash/noop';
import type { BiEvent } from '@wix/da-bi/pkg/events';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import BiOption from '@wix/da-bi/pkg/Components/BiOption';
import { useResizeObserver } from '@wix/da-hooks/pkg/useResizeObserver';
import {
  DropdownOption,
  NullableDropdownItem,
  DropdownGroup,
} from '../../../types';
import { isDropdownGroup, findSelectedItem } from '../../../helpers';
import { useDropdownItems } from '../../../hooks/useDropdownItems';
import s from './NativeSelectFieldContent.scss';

export interface NativeSelectFieldContentProps
  extends Omit<
    React.SelectHTMLAttributes<HTMLSelectElement>,
    'onSelect' | 'onChange' | 'value' | 'prefix'
  > {
  /** Required for a11y reasons. We need to be able to link auto-complete menus and labels to the input field. */
  id: string;
  className?: string;
  items: DropdownOption[];
  withBlankOption?: boolean;
  prefix?: React.ReactNode;
  hasError?: boolean;
  placeholder?: string;
  value?: React.SelectHTMLAttributes<HTMLSelectElement>['value'];
  biData?: BiEvent;
  openBiData?: BiEvent;
  closeBiData?: BiEvent;
  onSelect?: (value: any, event: any) => void;
}

const PLACEHOLDER_VALUE = '';
const DUMMY_OPTION_VALUE = '__da-native-select-dummy__';

function isPickerOpen(select: HTMLSelectElement): boolean {
  try {
    return select.matches(':open');
  } catch {
    return false; // :open not supported (non-base-select browsers)
  }
}

const NativeSelectFieldContent: React.FC<NativeSelectFieldContentProps> = ({
  items,
  value,
  withBlankOption = true,
  id,
  placeholder: placeholderProp,
  disabled,
  prefix,
  onSelect = noop,
  hasError,
  required,
  name,
  className,
  biData,
  openBiData = biData,
  closeBiData = biData,
  onClick,
  ...rest
}) => {
  const { t } = useTranslation();
  const placeholder = placeholderProp ?? t('common.select');
  const normalizedValue = Array.isArray(value) ? value[0] : value;
  const logBiEvent = useBiLogger();
  const [isOpen, setOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const selectRef = useRef<HTMLSelectElement>(null);
  const prefixRef = useRef<HTMLDivElement>(null);
  const suppressRootClickUntilRef = useRef(0);
  const suppressBlurCloseBiUntilRef = useRef(0);
  const scrolledRef = useRef(false);

  const updatePickerDimensions = useCallback(() => {
    if (!rootRef.current) {
      return;
    }
    const rootStyle = getComputedStyle(rootRef.current);
    const borderLeft = parseFloat(rootStyle.borderLeftWidth) || 0;
    const paddingLeft = parseFloat(rootStyle.paddingLeft) || 0;
    const paddingBottom = parseFloat(rootStyle.paddingBottom) || 0;
    const prefixEl = prefixRef.current;
    const prefixWidth = prefixEl
      ? prefixEl.offsetWidth +
        (parseFloat(getComputedStyle(prefixEl).marginRight) || 0)
      : 0;

    rootRef.current.style.setProperty(
      '--native-select-picker-width',
      `${rootRef.current.offsetWidth}px`
    );
    rootRef.current.style.setProperty(
      '--native-select-picker-offset',
      `${paddingLeft + prefixWidth + borderLeft}px`
    );
    rootRef.current.style.setProperty(
      '--native-select-picker-top-offset',
      `${paddingBottom}px`
    );
  }, []);

  useResizeObserver(rootRef, updatePickerDimensions);

  useEffect(() => {
    updatePickerDimensions();
  }, [updatePickerDimensions]);

  // Convert items to NullableDropdownItem format for useDropdownItems hook
  const dropdownItems: NullableDropdownItem[] = useMemo(
    () => [
      ...(withBlankOption
        ? [{ label: placeholder, value: PLACEHOLDER_VALUE }]
        : []),
      ...items.map(item => ({
        ...item,
        selected: `${item.value}` === `${normalizedValue}`,
      })),
    ],
    [withBlankOption, items, normalizedValue, placeholder]
  );

  const { filteredItems } = useDropdownItems(dropdownItems);
  const selectedItem = findSelectedItem(filteredItems);

  const pickerWillOverflow = filteredItems.length > 5;

  // Scroll-gradient affordance for base-select picker (Chrome 135+).
  // Options in the top-layer picker have real viewport coordinates, so we use
  // IntersectionObserver: option 1 entering = picker opened (reset gradient),
  // option 7 entering = user scrolled (hide gradient for this session).
  // Index 6 = 7th option = first one fully below the picker's 5-item viewport.
  useEffect(() => {
    if (!pickerWillOverflow || !selectRef.current) return;
    const options = selectRef.current.querySelectorAll('option');
    const firstOption = options[0];
    const scrollTarget = options[6];
    if (!firstOption || !scrollTarget) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.target === firstOption && entry.isIntersecting) {
            scrolledRef.current = false;
            setIsScrolled(false);
          } else if (
            entry.target === scrollTarget &&
            entry.isIntersecting &&
            !scrolledRef.current
          ) {
            scrolledRef.current = true;
            setIsScrolled(true);
          }
        }
      },
      { threshold: 0 }
    );
    observer.observe(firstOption);
    observer.observe(scrollTarget);
    return () => observer.disconnect();
  }, [pickerWillOverflow]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const selectedValue = event.target.value;
      const matchedItem = items.find(
        item => `${item.value}` === `${selectedValue}`
      );
      const resolvedValue =
        selectedValue === PLACEHOLDER_VALUE ||
        selectedValue === DUMMY_OPTION_VALUE
          ? PLACEHOLDER_VALUE
          : matchedItem
            ? matchedItem.value
            : selectedValue;
      if (isOpen) {
        closeBiData && logBiEvent(closeBiData);
        suppressBlurCloseBiUntilRef.current = Date.now() + 250;
      }
      // Prevent immediate click-through reopen from the same pointer action.
      suppressRootClickUntilRef.current = Date.now() + 250;
      setOpen(false);
      onSelect(resolvedValue, event);
    },
    [onSelect, items, isOpen, closeBiData, logBiEvent]
  );

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLSelectElement>) => {
      const pickerOpen =
        !!selectRef.current && isPickerOpen(selectRef.current);
      const wasOpen = pickerOpen || isOpen;
      if (wasOpen) {
        closeBiData && logBiEvent(closeBiData);
        suppressBlurCloseBiUntilRef.current = Date.now() + 250;
        setOpen(false);
      } else {
        openBiData && logBiEvent(openBiData);
        setOpen(true);
      }
      onClick && onClick(event);
    },
    [isOpen, openBiData, closeBiData, logBiEvent, onClick]
  );

  const handleRootPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (disabled || !selectRef.current) return;
      if (event.target === selectRef.current) return;

      const pickerOpen = isPickerOpen(selectRef.current) || isOpen;
      if (pickerOpen) {
        // Picker is open and user clicked root area — suppress the upcoming click
        // so handleRootClick doesn't reopen via showPicker()
        suppressRootClickUntilRef.current = Date.now() + 250;
        setOpen(false);
      }
    },
    [disabled, isOpen]
  );

  const handleBlur = useCallback(() => {
    if (Date.now() < suppressBlurCloseBiUntilRef.current) {
      setOpen(false);
      return;
    }
    if (isOpen) {
      closeBiData && logBiEvent(closeBiData);
    }
    setOpen(false);
  }, [isOpen, closeBiData, logBiEvent]);

  const handleRootClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (disabled || !selectRef.current) {
        return;
      }

      if (Date.now() < suppressRootClickUntilRef.current) {
        return;
      }

      if (event.target === selectRef.current) {
        return;
      }

      event.preventDefault();
      const selectWithPicker = selectRef.current as HTMLSelectElement & {
        showPicker?: () => void;
      };
      if (selectWithPicker.showPicker) {
        openBiData && logBiEvent(openBiData);
        setOpen(true);
        selectRef.current.focus();
        selectWithPicker.showPicker();
      } else {
        selectRef.current.click();
      }
    },
    [disabled, openBiData, logBiEvent]
  );

  const renderOption = ({
    value: v,
    label,
    hidden,
    disabled: optionDisabled,
    biData: optionBiData,
  }: DropdownOption) => (
    <BiOption
      key={v}
      hidden={hidden}
      value={v}
      disabled={optionDisabled}
      biData={optionBiData}
      label={label}
    >
      {label}
    </BiOption>
  );

  const renderOptGroup = ({
    label,
    disabled: groupDisabled,
    items: groupItems,
  }: DropdownGroup) => (
    <optgroup key={label} label={label} disabled={groupDisabled}>
      {groupItems.map(renderOption)}
    </optgroup>
  );

  const shouldUseDummyOption = !withBlankOption && !selectedItem;
  const hasProvidedValue =
    normalizedValue !== undefined &&
    normalizedValue !== null &&
    (withBlankOption || `${normalizedValue}` !== PLACEHOLDER_VALUE);
  const selectValue =
    hasProvidedValue
      ? normalizedValue
      : selectedItem
        ? selectedItem.value
        : shouldUseDummyOption
          ? DUMMY_OPTION_VALUE
          : PLACEHOLDER_VALUE;
  const isPlaceholder =
    selectValue === PLACEHOLDER_VALUE || selectValue === DUMMY_OPTION_VALUE;

  return (
    <div
      ref={rootRef}
      onPointerDownCapture={handleRootPointerDown}
      onClick={handleRootClick}
      className={c(
        s['native-root'],
        hasError && s['error'],
        disabled && s['disabled'],
        className
      )}
    >
      {prefix && <div ref={prefixRef} className={s['prefix']}>{prefix}</div>}
      <select
        ref={selectRef}
        id={id}
        name={name}
        className={c(
          s['native-select'],
          'ignore-react-onclickoutside',
          isPlaceholder && s['placeholder-selected'],
          pickerWillOverflow && !isScrolled && s['has-scroll']
        )}
        value={selectValue}
        disabled={disabled}
        required={required}
        onClick={handleClick}
        onBlur={handleBlur}
        onChange={handleChange}
        {...rest}
      >
        {shouldUseDummyOption && (
          <option value={DUMMY_OPTION_VALUE} hidden disabled>
            {placeholder}
          </option>
        )}
        {filteredItems.map(item =>
          isDropdownGroup(item) ? renderOptGroup(item) : renderOption(item)
        )}
      </select>
    </div>
  );
};

NativeSelectFieldContent.displayName = 'NativeSelectFieldContent';

export default NativeSelectFieldContent;
